import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

import { resetUserPassword } from '@/hooks/agent';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ButtonLoading } from '@/components/common/button/loading-button';

const passwordResetSchema = z
  .object({
    password: z
      .string({
        required_error: 'Password is required',
      })
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
    confirmPassword: z
      .string({
        required_error: 'Please confirm your password',
      })
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

const ResetPasswordDialog = ({ isOpen, onOpenChange, agent }) => {
  const form = useForm({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const onSubmit = async (data) => {
    if (!agent) return;

    try {
      await resetUserPassword(agent._id, data.password);
      toast.success('Password reset successfully');
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reset password');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {agent && (
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {agent.first_name} {agent.last_name}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder='Enter new password'
                        {...field}
                      />
                    </FormControl>
                    <p className='text-xs text-muted-foreground'>
                      Password must be at least 8 characters long
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder='Confirm new password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className='mt-5'>
                <div className='flex w-full flex-row items-center justify-end'>
                  <div className='flex space-x-2'>
                    <Button
                      type='button'
                      variant='secondary'
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    {form.formState.isSubmitting ? (
                      <ButtonLoading />
                    ) : (
                      <Button type='submit'>Reset Password</Button>
                    )}
                  </div>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ResetPasswordDialog;
